import { ArrowRight, Check } from 'lucide-react';
import { Modal } from './Modal';
import { Button } from './Button';
import type { SubscriptionTier } from '@maker-toolchest/shared';
import { TIER_LIMITS } from '@maker-toolchest/shared';

interface TierUpgradeModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  currentTier: SubscriptionTier;
  targetTier: SubscriptionTier;
  loading?: boolean;
}

const TIER_PRICES: Record<SubscriptionTier, string> = {
  TINKERER: 'Free',
  HOBBYIST: '$12/month',
  BUSINESS: '$49/month',
};

const TIER_NAMES: Record<SubscriptionTier, string> = {
  TINKERER: 'Tinkerer',
  HOBBYIST: 'Hobbyist',
  BUSINESS: 'Business',
};

export function TierUpgradeModal({
  open,
  onClose,
  onConfirm,
  currentTier,
  targetTier,
  loading = false,
}: TierUpgradeModalProps) {
  const currentLimits = TIER_LIMITS[currentTier];
  const targetLimits = TIER_LIMITS[targetTier];

  const formatLimit = (limit: number): string => {
    return limit === Infinity ? 'Unlimited' : limit.toString();
  };

  return (
    <Modal open={open} onClose={onClose} size="lg">
      <div className="flex flex-col gap-6">
        {/* Header */}
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            Upgrade to {TIER_NAMES[targetTier]}
          </h2>
          <p className="mt-1 text-sm text-foreground-muted">
            Compare your current plan with the upgraded features
          </p>
        </div>

        {/* Comparison Grid */}
        <div className="grid grid-cols-2 gap-4">
          {/* Current Tier */}
          <div className="rounded-lg border border-border bg-background-subtle p-4">
            <div className="mb-3">
              <h3 className="text-sm font-medium text-foreground">
                {TIER_NAMES[currentTier]}
              </h3>
              <p className="text-xs text-foreground-muted">{TIER_PRICES[currentTier]}</p>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground-muted">Printers</span>
                <span className="font-medium text-foreground">
                  {formatLimit(currentLimits.printers)}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground-muted">Filaments</span>
                <span className="font-medium text-foreground">
                  {formatLimit(currentLimits.filaments)}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground-muted">Team Members</span>
                <span className="font-medium text-foreground">
                  {formatLimit(currentLimits.teamMembers)}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground-muted">Storage</span>
                <span className="font-medium text-foreground">
                  {currentLimits.storageMB === Infinity
                    ? 'Unlimited'
                    : currentLimits.storageMB >= 1024
                      ? `${currentLimits.storageMB / 1024} GB`
                      : `${currentLimits.storageMB} MB`}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground-muted">Projects</span>
                <span className="font-medium text-foreground">
                  {currentLimits.hasProjects ? (
                    <Check className="h-4 w-4 text-success" />
                  ) : (
                    <span className="text-foreground-muted">—</span>
                  )}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground-muted">Cost Tracking</span>
                <span className="font-medium text-foreground">
                  {currentLimits.hasCostTracking ? (
                    <Check className="h-4 w-4 text-success" />
                  ) : (
                    <span className="text-foreground-muted">—</span>
                  )}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground-muted">Filament Tracking</span>
                <span className="font-medium text-foreground capitalize">
                  {currentLimits.filamentTracking}
                </span>
              </div>
            </div>
          </div>

          {/* Target Tier */}
          <div className="rounded-lg border-2 border-primary bg-primary/5 p-4">
            <div className="mb-3">
              <h3 className="text-sm font-medium text-foreground">
                {TIER_NAMES[targetTier]}
              </h3>
              <p className="text-xs text-primary">{TIER_PRICES[targetTier]}</p>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground-muted">Printers</span>
                <span className="font-medium text-primary">
                  {formatLimit(targetLimits.printers)}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground-muted">Filaments</span>
                <span className="font-medium text-primary">
                  {formatLimit(targetLimits.filaments)}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground-muted">Team Members</span>
                <span className="font-medium text-primary">
                  {formatLimit(targetLimits.teamMembers)}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground-muted">Storage</span>
                <span className="font-medium text-primary">
                  {targetLimits.storageMB === Infinity
                    ? 'Unlimited'
                    : targetLimits.storageMB >= 1024
                      ? `${targetLimits.storageMB / 1024} GB`
                      : `${targetLimits.storageMB} MB`}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground-muted">Projects</span>
                <span className="font-medium text-foreground">
                  {targetLimits.hasProjects ? (
                    <Check className="h-4 w-4 text-success" />
                  ) : (
                    <span className="text-foreground-muted">—</span>
                  )}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground-muted">Cost Tracking</span>
                <span className="font-medium text-foreground">
                  {targetLimits.hasCostTracking ? (
                    <Check className="h-4 w-4 text-success" />
                  ) : (
                    <span className="text-foreground-muted">—</span>
                  )}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground-muted">Filament Tracking</span>
                <span className="font-medium text-primary capitalize">
                  {targetLimits.filamentTracking}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 border-t border-border pt-4">
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={onConfirm}
            loading={loading}
            rightIcon={<ArrowRight className="h-4 w-4" />}
          >
            Continue to Upgrade
          </Button>
        </div>
      </div>
    </Modal>
  );
}
